import moment from "moment";

import { PatientHelper } from "@/Common/Helpers/PatientHelper";
import { IPatientCreationModel } from "@/Common/Models/Patient/UI/IPatientCreationModel";
import { Person } from "@/Common/Models/Person/Person";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { LocalEvent } from "@/Common/Stores/Actions/LocalEvent";
import { BaseOperationHandler } from "@/Common/Stores/Handlers/Local/BaseOperationHandler";
import { SidebarStore } from "@/Common/Stores/SidebarStore";

class SidebarHandler extends BaseOperationHandler {
    private searchDebounceReference: number;

    constructor() {
        super();

        this.RegisterOperation(LocalOperations.Sidebar.Open, this.openSidebar);
        this.RegisterOperation(LocalOperations.Sidebar.Close, this.closeSidebar);
        this.RegisterOperation(LocalOperations.Sidebar.AutoOpenClose, this.autoOpenClose);
        this.RegisterOperation(LocalOperations.Sidebar.ToggleSidebar, this.toggleSidebar);
        this.RegisterOperation(LocalOperations.Sidebar.Today_Click, this.todayClick);
        this.RegisterOperation(LocalOperations.Router.Route_Change, this.autoOpenClose);
        this.RegisterOperation(LocalOperations.Sidebar.DateInput_Blur, this.dateInput_Blur);
        this.RegisterOperation(LocalOperations.Sidebar.DateInput_Changed, this.dateInput_Changed);
        this.RegisterOperation(LocalOperations.Sidebar.Calendar_SelectDate, this.onCalDateSelected);
        this.RegisterOperation(LocalOperations.Sidebar.SearchTextChanged, this.onSearchTextChanged);
        this.RegisterOperation(LocalOperations.Sidebar.SearchIsInactiveChanged, this.onSearchInactiveChanged);
        this.RegisterOperation(LocalOperations.Sidebar.SearchInPortalChecked, this.searchInPortalChecked);
        this.RegisterOperation(LocalOperations.Sidebar.SearchInRelativesChanged, this.searchInRelativesChanged);
        this.RegisterOperation(LocalOperations.Sidebar.Select_Patient, this.onSelectPatient, false);
        this.RegisterOperation(LocalOperations.Sidebar.Select_Patient_Duplicate, this.onSelectPatientDuplicate, false);
        this.RegisterOperation(LocalOperations.Sidebar.CreatePatient, this.onCreatePatient);
        this.RegisterOperation(LocalOperations.Sidebar.CreatePatient_Cancel, this.onCreatePatient_Cancel);
        this.RegisterOperation(LocalOperations.Sidebar.CreatePatient_Save, this.onCreatePatient_Save);
        this.RegisterOperation(LocalOperations.Sidebar.ToggleAdvancedSearch, this.toggleAdvancedSearch);
        this.RegisterOperation(LocalOperations.Sidebar.SelectPreviousPatient, this.selectPreviousPatient, false);
        this.RegisterOperation(LocalOperations.Sidebar.SelectNextPatient, this.selectNextPatient, false);
        this.RegisterOperation(LocalOperations.Sidebar.SelectPatientWithEnterKey, this.selectPatientWithEnterKey, false);
        this.RegisterOperation(LocalOperations.Sidebar.Sidebar_LoadNotice_Counts, this.loadNoticeCounts);

        this.searchDebounceReference = -1;
    }

    private selectPatientWithEnterKey(request: LocalEvent, store: SidebarStore): void {
        let personToOpen = null;
        for (let i = 0; i < store.Model.get("SearchResultsPatients").size; i++) {
            let p = store.Model.get("SearchResultsPatients").get(i);
            if (p.ContactId === store.Model.get("SelectedPatientId")) {
                personToOpen = p;
                break;
            }
        }

        if (personToOpen != null) {
            window.setTimeout(
                () => {
                    let change = new LocalEvent(LocalOperations.Sidebar.Select_Patient);
                    change.AddItem("person", personToOpen);
                    LocalEventDispatcher.dispatch(change);
                },
                50
            );
        }
    }

    private selectPreviousPatient(request: LocalEvent, store: SidebarStore): void {
        if (store.Model.get("SearchResultsPatients").size === 0) { return; }

        function indexOfCurrent(contactId: string): number {
            if (contactId == null || contactId == "")
                return -1;
            for (let i = 0; i < store.Model.get("SearchResultsPatients").size; i++) {
                let q = store.Model.get("SearchResultsPatients").get(i);
                if (q.ContactId === contactId) {
                    return i;
                }
            }
            return -1;
        }

        let idx = indexOfCurrent(store.Model.get("SelectedPatientId"));

        if (idx - 1 < 0) {
            // focus on field
            store.Model = store.Model.set("SelectedPatientId", null);
            store.EmitChangeToListeners();
            window.setTimeout(
                () => $("#__patientSearchField").focus(),
                50
            );
        }
        else {
            idx -= 1;
            store.Model = store.Model.set("SelectedPatientId", store.Model.get("SearchResultsPatients").get(idx).ContactId);
            store.EmitChangeToListeners();
            window.setTimeout(
                () => $("#patientContainer").find(".sidebar-item.selected").focus(),
                50
            );
        }
    }

    private selectNextPatient(request: LocalEvent, store: SidebarStore): void {
        if (store.Model.get("SearchResultsPatients").size === 0) { return; }

        function indexOfCurrent(contactId: string): number {
            if (contactId == null || contactId == "")
                return -1;
            for (let i = 0; i < store.Model.get("SearchResultsPatients").size; i++) {
                let q = store.Model.get("SearchResultsPatients").get(i);
                if (q.ContactId === contactId) {
                    return i;
                }
            }
            return -1;
        }

        let idx = indexOfCurrent(store.Model.get("SelectedPatientId"));

        if (idx + 1 < store.Model.get("SearchResultsPatients").size) { idx += 1; }

        store.Model = store.Model.set("SelectedPatientId", store.Model.get("SearchResultsPatients").get(idx).ContactId);
        store.EmitChangeToListeners();

        window.setTimeout(
            () => $("#patientContainer").find(".sidebar-item.selected").focus(),
            50
        );
    }

    private onCreatePatient_Cancel(request: LocalEvent, store: SidebarStore): void {
        store.Model = sidebarStore.Model.set("IsCreateMode", false);
    }

    private onCreatePatient_Save(request: LocalEvent, store: SidebarStore): void {
        let patientModel: IPatientCreationModel = request.Get<IPatientCreationModel>("patientEditModel");
        store.Actions.CreateNewPatient(patientModel);
    }

    private onCreatePatient(request: LocalEvent, store: SidebarStore): void {
        store.Model = sidebarStore.Model
            .set("IsCreateMode", !sidebarStore.Model.get("IsCreateMode"))
            .set("PatientCreationModel", Immutable.fromJS(PatientHelper.getDefaultPatientCreationModel()));
    }

    private onSelectPatient(request: LocalEvent, store: SidebarStore): void {
        let person: Person = request.Get<Person>("person");

        if (store.Model.get("SearchInPortalSelected") === true && person.ContactId == null) {
            store.Model = sidebarStore.Model.set("SelectedPatientId", person.ContactId);
            let op = new LocalEvent(LocalOperations.MainComponentViewOperations.DisplaySyncModal);
            op.AddItem("client", person);
            MainComponentStateDispatcher.dispatch(op);
        }
        else {
            sessionStore.dispatch(LocalOperations.Person.LoadFile, { contactId: person.ContactId, contactFacetType: person.ContactFacetType }, 0);
            store.EmitChangeToListeners();
        }
    }

    private onSelectPatientDuplicate(request: LocalEvent, store: SidebarStore): void {
        const person: Person = request.Get<Person>("person");
        this.onCreatePatient_Cancel(request, store);
        this.closeSidebar(request, store);

        if (store.Model.get("SearchInPortalSelected") && IsNullOrWhiteSpace(person.PortalProfileId)) {
            const operation = new LocalEvent(LocalOperations.MainComponentViewOperations.DisplaySyncModal);
            operation.AddItem("client", person);
            MainComponentStateDispatcher.dispatch(operation);
        }
        else {
            store.Model = sidebarStore.Model.set("SelectedPatientId", person.ContactFacetId);
            sessionStore.dispatch(LocalOperations.Person.LoadFile, { contactId: person.ContactId, contactFacetType: person.ContactFacetType }, 0);
            store.EmitChangeToListeners();
        }
    }

    private onSearchInactiveChanged(request: LocalEvent, store: SidebarStore): void {
        store.Model = sidebarStore.Model.set("SearchIsInactiveSelected", !store.Model.get("SearchIsInactiveSelected"));
        request = new LocalEvent(LocalOperations.Sidebar.SearchTextChanged);
        request.AddItem("value", store.Model.get("SearchText"));
        this.onSearchTextChanged(request, store);
    }

    private searchInPortalChecked(request: LocalEvent, store: SidebarStore): void {
        // set "portal mode" for patient search in patient store , clear search results patient
        const usePortal: boolean = !store.Model.get("SearchInPortalSelected");
        store.Model = store.Model.set("SearchInPortalSelected", usePortal);
        store.Model = store.Model.set("SearchResultsPatients", Immutable.List([]));

        const event = new LocalEvent(LocalOperations.Sidebar.SearchTextChanged);
        event.AddItem("value", store.Model.get("SearchText"));
        return this.onSearchTextChanged(event, store);
    }

    private searchInRelativesChanged(request: LocalEvent, store: SidebarStore): void {
        const searchRelativesSelected: boolean = !store.Model.get("SearchInRelativesSelected");
        store.Model = store.Model.set("SearchInRelativesSelected", searchRelativesSelected);
        store.Model = store.Model.set("SearchResultsPatients", Immutable.List([]));

        const event = new LocalEvent(LocalOperations.Sidebar.SearchTextChanged);
        event.AddItem("value", store.Model.get("SearchText"));
        this.onSearchTextChanged(event, store);
    }

    private onSearchTextChanged(request: LocalEvent, store: SidebarStore): void {
        const value = request.Get<string>("value");
        if (IsNullOrUndefined(value))
            return;

        store.Model = store.Model.set("SearchText", value);

        const portalSearch = store.Model.get("SearchInPortalSelected");
        const relativeSearch = store.Model.get("SearchInRelativesSelected");

        // handler type debounce
        if (this.searchDebounceReference !== -1) {
            clearTimeout(this.searchDebounceReference);
            this.searchDebounceReference = -1;
        }
        this.searchDebounceReference = window.setTimeout(
            () => {
                // update search ID to avoid unordered responses
                sidebarStore.currentSearchId++;

                const isInactiveSearch = store.Model.get("SearchIsInactiveSelected");
                const maxNumOfResults = store.Model.get("SearchMaxResults");

                // TODO Relatives don't have portal accounts, this condition will need to get updated when they do have one
                if (store.Model.get("IsAdvancedSearchView") || (relativeSearch && portalSearch))
                    return;

                if (portalSearch) {
                    store.Actions.SearchPortalPatients(value);
                }
                else if (relativeSearch) {
                    store.Actions.SearchRelatives(value, isInactiveSearch, maxNumOfResults);
                }
                else {
                    store.Actions.SearchPatients(value, isInactiveSearch, maxNumOfResults);
                }
            },
            400
        );
    }

    private todayClick(request: LocalEvent, store: SidebarStore): void {
        store.Model = store.Model.set("SelectedDate", moment().toDate());
        store.Model = store.Model.set("DateInputText", moment().format("YYYY-MM-DD"));
        this.dispatchDateChangeEvent();
    }

    private dateInput_Changed(request: LocalEvent, store: SidebarStore): void {
        store.Model = store.Model.set("DateInputText", request.Get<string>("value"));
    }

    private dateInput_Blur(request: LocalEvent, store: SidebarStore): void {
        let currentValue = store.Model.get("DateInputText");

        let date = kendo.parseDate(currentValue, ["yyyy-MM-dd", "yyyy/MM/dd", "yyyyMMdd"]);
        if (date == null) {
            store.Model = store.Model.set("DateInputText", "");
        }
        else {
            store.Model = store.Model.set("DateInputText", moment(date).format("YYYY-MM-DD"));
            store.Model = store.Model.set("SelectedDate", date);
            this.dispatchDateChangeEvent();
        }
    }

    private autoOpenClose(request: LocalEvent, store: SidebarStore): void {
        store.autoOpenClose();
    }

    private openSidebar(request: LocalEvent, store: SidebarStore): void {
        store.openSidebar();
    }

    private closeSidebar(request: LocalEvent, store: SidebarStore): void {
        store.closeSidebar();
    }

    private toggleSidebar(request: LocalEvent, store: SidebarStore): void {
        store.toggleOpenClose();
    }

    private onCalDateSelected(request: LocalEvent, store: SidebarStore): void {
        let dt = request.Get<Date>("value");
        store.Model = store.Model.set("DateInputText", moment(dt).format("YYYY-MM-DD"));
        store.Model = store.Model.set("SelectedDate", dt);
        this.dispatchDateChangeEvent();
    }

    private toggleAdvancedSearch(request: LocalEvent, store: SidebarStore): void {
        store.Model = store.Model.set("IsAdvancedSearchView", !store.Model.get("IsAdvancedSearchView"));
    }

    private dispatchDateChangeEvent(): void {
        window.setTimeout(() => {
            let operation = new LocalEvent(LocalOperations.Sidebar.DateChanged);
            operation.AddItem("value", sidebarStore.Model.get("SelectedDate"));
            LocalEventDispatcher.dispatch(operation);
        }, 0);
    }

    private loadNoticeCounts(): void {
        sidebarStore.Actions.GetNoticeCounts();
    }
}

export { SidebarHandler };
